import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../apiClient'; // <-- Your API Client
import { Link } from 'react-router-dom';

// --- MUI Components ---
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert, // Keep Alert for page-load errors
  Box, // Use Box for layout instead of Container
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar, // 1. ✅ Import Snackbar for action feedback
  Chip // 2. ✅ Import Chip for styling job type
} from '@mui/material';

// --- MUI Icons ---
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

// A helper function to format the date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function JobList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null); // For page-load errors

  // --- State for Delete Confirmation Modal ---
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);

  // 3. ✅ State for Snackbar feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' or 'error'
  });

  // --- Data Fetching ---
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const response = await apiClient.get('/careers/jobs'); 
      setJobs(response.data);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      let errorMsg = 'Failed to fetch job postings. Please try again later.';
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        errorMsg = 'You are not authorized. Please log out and log in again.';
      }
      setPageError(errorMsg); // Use pageError for load failures
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // --- Delete Logic ---
  const handleOpenDeleteDialog = (id) => {
    setSelectedJobId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedJobId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!selectedJobId) return;

    try {
      await apiClient.delete(`/careers/jobs/${selectedJobId}`);
      setJobs(prevJobs => prevJobs.filter(job => job._id !== selectedJobId));
      // 4. ✅ Show SUCCESS snackbar
      setSnackbar({ open: true, message: 'Job deleted successfully', severity: 'success' });

    } catch (err) {
      console.error('Error deleting job:', err);
      // 5. ✅ Show ERROR snackbar
      setSnackbar({ open: true, message: 'Failed to delete job posting. Please try again.', severity: 'error' });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // 6. ✅ Handler to close the snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // --- Render Loading State ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Render Error State ---
  if (pageError) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>{pageError}</Alert>
    );
  }

  // --- Main Render ---
  return (
    // 7. ✅ Use <Box> to fit cleanly in AdminLayout
    <Box>
      {/* 1. Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Job Postings
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/admin/career/new" 
          startIcon={<AddIcon />}
        >
          Add New Job
        </Button>
      </Box>

      {/* 2. The Data Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Table sx={{ minWidth: 650 }} aria-label="job postings table">
          <TableHead>
            {/* 8. ✅ Apply clean, light header style */}
            <TableRow sx={{ '& th': { fontWeight: '600', backgroundColor: '#f9fafb' } }}>
              <TableCell>Title</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Location</TableCell>
              {/* 9. ✅ ADDED Type/Workplace column */}
              <TableCell>Type</TableCell>
              {/* 10. ✅ ADDED Date column */}
              <TableCell>Date Posted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                {/* 11. ✅ Update colSpan to 7 */}
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No job postings found.
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => (
                <TableRow
                  key={job._id}
                  // 12. ✅ Add subtle hover effect
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#f9fafb' } }}
                >
                  {/* 13. ✅ Make title bolder */}
                  <TableCell sx={{ fontWeight: 500 }}>{job.title}</TableCell>
                  <TableCell>{job.website}</TableCell>
                  <TableCell>{job.department}</TableCell>
                  <TableCell>{job.location}</TableCell>
                  {/* 14. ✅ Render Type/Workplace with a Chip */}
                  <TableCell>
                    <Chip 
                      label={job.type} 
                      size="small" 
                      color={job.type === 'Full-time' ? 'primary' : 'default'} 
                      variant="outlined" 
                      sx={{ mr: 0.5 }} 
                    />
                    <Chip 
                      label={job.workplace} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  {/* 15. ✅ Render Date */}
                  <TableCell>{formatDate(job.createdAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      component={Link}
                      to={`/admin/career/edit/${job._id}`} 
                      color="primary"
                      aria-label="edit job"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(job._id)}
                      aria-label="delete job"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 3. Delete Confirmation Modal (Unchanged) */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this job posting? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 4. ✅ Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}